import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios, { AxiosError } from 'axios';
import { toast } from 'react-hot-toast';
import React from 'react';

// Tipos
export interface User {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    phone?: string;
    profile_image?: string;
    user_type: 'customer' | 'seller' | 'admin';
    status: 'active' | 'inactive' | 'suspended' | 'banned';
    seller_status?: 'pending' | 'approved' | 'rejected';
    created_at: string;
    updated_at: string;
    seller_id?: number;
}

export interface AuthTokens {
    access_token: string;
    refresh_token: string;
    expires_in: number;
}

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface RegisterData {
    username: string;
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone?: string;
    user_type?: 'customer' | 'seller';
}

export interface AuthResponseData {
    user: User;
    access_token: string;
    refresh_token: string;
    expires_in: number;
}

export interface AuthResponse {
    success: boolean;
    data: AuthResponseData;
    message: string;
}

interface AuthState {
    user: User | null;
    tokens: AuthTokens | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    lastUpdate: number; // Nueva propiedad para rastrear actualizaciones

    // Acciones
    login: (credentials: LoginCredentials) => Promise<void>;
    register: (data: RegisterData) => Promise<void>;
    logout: (flag?: boolean) => void;
    refreshToken: () => Promise<void>;
    updateProfile: (data: Partial<User>) => Promise<void>;
    clearError: () => void;
    forceUpdate: () => void; // Nueva acción para forzar actualización

    // Estado interno
    setLoading: (loading: boolean) => void;
    setUser: (user: User | null) => void;
    setTokens: (tokens: AuthTokens | null) => void;
}

// Configuración de axios
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

const api = axios.create({
    baseURL: `${API_BASE_URL}/api/v1`,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor para agregar token automáticamente
api.interceptors.request.use((config) => {
    const state = useAuthStore.getState();
    if (state.tokens?.access_token) {
        config.headers.Authorization = `Bearer ${state.tokens.access_token}`;
    }
    return config;
});

// Interceptor para manejar respuestas y refresh token
api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as any;
        const isAuthEndpoint =
            originalRequest?.url?.includes('/auth/login') ||
            originalRequest?.url?.includes('/auth/register') ||
            originalRequest?.url?.includes('/auth/refresh');

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            originalRequest._retry = true;

            try {
                await useAuthStore.getState().refreshToken();
                const newToken = useAuthStore.getState().tokens?.access_token;
                if (newToken) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return api(originalRequest);
                }
            } catch (refreshError) {
                useAuthStore.getState().logout(false);
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            // Estado inicial
            user: null,
            tokens: null,
            isLoading: false,
            isAuthenticated: false,
            lastUpdate: Date.now(),

            // Acciones
            login: async (credentials: LoginCredentials) => {
                try {
                    set({ isLoading: true });
                    const response = await api.post<AuthResponse>('/auth/login', credentials);
                    const { user, access_token, refresh_token, expires_in } = response.data.data;
                    const tokens: AuthTokens = {
                        access_token,
                        refresh_token,
                        expires_in,
                    };

                    set({
                        user,
                        tokens,
                        isAuthenticated: true,
                        isLoading: false,
                        lastUpdate: Date.now(),
                    });

                    // Forzar sincronización con el almacenamiento persistente
                    //await useAuthStore.persist.setOptions({ name: 'auth-storage' }).sync();
                    useAuthStore.persist.rehydrate();
                    //console.log('Rehydrated state:', useAuthStore.getState());
                    toast.success('¡Bienvenido de vuelta!');
                } catch (error: any) {
                    set({ isLoading: false });
                    console.log("veo el error2", error)
                    if (error.response) {
                        const message = error.response.data?.message || 'Error al iniciar sesión';
                        toast.error(message);
                    } else if (error.request) {
                        toast.error('Error de conexión. Verifica tu internet.');
                    } else {
                        toast.error('Error inesperado al iniciar sesión');
                    }
                    throw error;
                }
            },

            register: async (data: RegisterData) => {
                try {
                    set({ isLoading: true });
                    const response = await api.post<AuthResponse>('/auth/register', {
                        ...data,
                        password_confirmation: data.password,
                    });

                    const { user, access_token, refresh_token, expires_in } = response.data.data;
                    const tokens: AuthTokens = {
                        access_token,
                        refresh_token,
                        expires_in,
                    };

                    set({
                        user,
                        tokens,
                        isAuthenticated: true,
                        isLoading: false,
                        lastUpdate: Date.now(),
                    });

                    //await useAuthStore.persist.setOptions({ name: 'auth-storage' }).sync();
                    useAuthStore.persist.rehydrate();
                    toast.success('¡Cuenta creada exitosamente!');
                } catch (error: any) {
                    set({ isLoading: false });
                    const defaultMessage = 'Error al crear la cuenta';
                    const errors = error?.response?.data?.errors;
                    const message = error?.response?.data?.message;

                    if (errors && typeof errors === 'object') {
                        // Extrae y combina los mensajes en un solo string
                        const detalles = Object.values(errors)
                            .flat()
                            .join('\n'); // O usar '<br />' si estás mostrando HTML
                        toast.error(`${message}\n${detalles}`);
                    } else {
                        toast.error(message || defaultMessage);
                    }
                    //throw error;
                }
            },

            logout: (showNotification = true) => {
                set({
                    user: null,
                    tokens: null,
                    isAuthenticated: false,
                    isLoading: false,
                    lastUpdate: Date.now(),
                });

                localStorage.removeItem('auth-storage');
                if (showNotification)
                    toast.success('Sesión cerrada correctamente');
            },

            refreshToken: async () => {
                try {
                    const { tokens } = get();
                    if (!tokens?.refresh_token) throw new Error('No hay refresh token disponible');

                    const response = await api.post<AuthResponse>('/auth/refresh', {
                        refresh_token: tokens.refresh_token,
                    });

                    const { access_token, refresh_token, expires_in } = response.data.data;
                    const newTokens: AuthTokens = {
                        access_token,
                        refresh_token,
                        expires_in,
                    };

                    set({
                        tokens: newTokens,
                        lastUpdate: Date.now(),
                    });

                    //await useAuthStore.persist.setOptions({ name: 'auth-storage' }).sync();
                    useAuthStore.persist.rehydrate();
                } catch (error) {
                    get().logout(false);
                    throw error;
                }
            },

            updateProfile: async (data: Partial<User>) => {
                try {
                    set({ isLoading: true });
                    const response = await api.put<{ user: User }>('/auth/profile', data);
                    const { user } = response.data;

                    set({
                        user,
                        isLoading: false,
                        lastUpdate: Date.now(), // Actualizar marca de tiempo
                    });

                    // Forzar sincronización inmediata
                    //await useAuthStore.persist.setOptions({ name: 'auth-storage' }).sync();
                    useAuthStore.persist.rehydrate();

                    toast.success('Perfil actualizado correctamente');
                } catch (error: any) {
                    set({ isLoading: false });
                    const message = error.response?.data?.message || 'Error al actualizar perfil';
                    toast.error(message);
                    throw error;
                }
            },

            clearError: () => {
                // Implementar si es necesario
            },

            forceUpdate: () => {
                set({ lastUpdate: Date.now() });
            },

            // Métodos de utilidad
            setLoading: (loading: boolean) => set({ isLoading: loading }),
            setUser: (user: User | null) => set({
                user,
                lastUpdate: Date.now()
            }),
            setTokens: (tokens: AuthTokens | null) => set({
                tokens,
                lastUpdate: Date.now()
            }),
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                tokens: state.tokens,
                isAuthenticated: state.isAuthenticated,
                lastUpdate: state.lastUpdate,
            }),
            version: 1,
            onRehydrateStorage: () => (state) => {
                if (state) {
                    //console.log('Estado rehidratado:', state.user);
                }
            },
        }
    )
);

// Hook personalizado para verificar roles
export const useAuthRole = () => {
    const user = useAuthStore((state) => state.user);
    //const lastUpdate = useAuthStore((state) => state.lastUpdate); // Suscribirse a lastUpdate

    return {
        isCustomer: user?.user_type === 'customer',
        isSeller: user?.user_type === 'seller',
        isAdmin: user?.user_type === 'admin',
        hasRole: (role: string) => user?.user_type === role,
        hasAnyRole: (roles: string[]) => user ? roles.includes(user.user_type) : false,
    };
};

// Hook para verificar autenticación (actualizado)
export const useAuth = () => {
    const { user, isAuthenticated, isLoading, lastUpdate } = useAuthStore();

    // Forzar re-render cuando lastUpdate cambie
    React.useEffect(() => { }, [lastUpdate]);

    return {
        user,
        isAuthenticated,
        isLoading,
        isGuest: !isAuthenticated,
        lastUpdate, // Exponer lastUpdate si es necesario
    };
};

export default useAuthStore;